import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom'

import { IdleLanguageCorner, LanguageBar } from '@/design-system/LanguageSwitcher'
import { AnalyticsPage } from '@/features/analytics/pages/AnalyticsPage'
import { CheckinPage } from '@/features/checkin/pages/CheckinPage'
import { ApplyPage } from '@/features/competition/pages/ApplyPage'
import { CandidatesPage } from '@/features/competition/pages/CandidatesPage'
import { JuryPage } from '@/features/competition/pages/JuryPage'
import { MyResultsPage } from '@/features/competition/pages/MyResultsPage'
import { EnvironmentBanner } from '@/features/environment/components/EnvironmentBanner'
import { PageViewTracker } from '@/features/event/PageViewTracker'
import { FeedbackPage } from '@/features/feedback/FeedbackPage'
import { HomePage } from '@/features/event/pages/HomePage'
import { InfoPage } from '@/features/event/pages/InfoPage'
import { MenuPage } from '@/features/event/pages/MenuPage'
import { TableCustomizePage } from '@/features/tables/TableCustomizePage'
import { ProgramPage } from '@/features/event/pages/ProgramPage'
import { ResultsPage } from '@/features/event/pages/ResultsPage'
import { LegalPage } from '@/features/event/pages/LegalPage'
import { MyDataPage } from '@/features/event/pages/MyDataPage'
import { TeamPage } from '@/features/event/pages/TeamPage'
import { AdminApp } from '@/features/admin/pages/AdminApp'
import { CashPage } from '@/features/onsite/pages/CashPage'
import { OnsitePage } from '@/features/onsite/pages/OnsitePage'
import { InventoryPage } from '@/features/inventory/pages/InventoryPage'
import { PosPage } from '@/features/pos/pages/PosPage'
import { LivePage } from '@/features/live/pages/LivePage'
import { StationPage } from '@/features/live/pages/StationPage'
import { ServicePage } from '@/features/service/pages/ServicePage'
import { MyTicketsPage } from '@/features/me/pages/MyTicketsPage'
import { ConductorPage } from '@/features/program/pages/ConductorPage'
import { ScreenPage } from '@/features/program/pages/ScreenPage'
import { VotePage } from '@/features/voting/pages/VotePage'
import { OrderPage } from '@/features/tickets/pages/OrderPage'
import { TicketsPage } from '@/features/tickets/pages/TicketsPage'

/**
 * Routage public.
 *
 * Rien de ce qui s'affiche n'est écrit dans le code : nom, slogan, date, lieu,
 * blocs éditoriaux, FAQ, partenaires, billets, prix et couleurs viennent tous
 * de l'API publique, donc de l'administration.
 *
 * `/mon-billet` s'ouvre avec le jeton reçu à la commande — sans compte.
 * `/ops/checkin` et `/ops/deroule` demandent une session équipe et le droit
 * correspondant. `/ecran` n'a pas d'utilisateur : une clé d'affichage suffit.
 *
 * La surface `/admin` arrive avec sa phase.
 */
export default function App() {
  return (
    <BrowserRouter>
      {/* Hors des routes : présent sur toutes les pages, public, admin et ops. */}
      <EnvironmentBanner />
      <PageViewTracker />
      <AnimatedRoutes />
    </BrowserRouter>
  )
}

/**
 * Les pages qu'on ouvre depuis un lien personnel ou un QR — vote, juré,
 * billet, suivi de commande — n'ont pas l'en-tête du site. Or ce sont celles
 * où l'invité étranger arrive sans avoir rien choisi : la langue se règle
 * ici, une fois pour toutes ces pages, y compris leurs états d'erreur
 * (« lien invalide » doit aussi se lire en anglais).
 */
function WithLanguageBar({ children }: { children: React.ReactNode }) {
  return (
    <>
      <LanguageBar />
      {children}
    </>
  )
}

/**
 * Chaque rubrique publique entre en fondu, en sortant d'un léger flou.
 *
 * La clé de transition est la PAGE publique, mais tout le back-office en
 * partage une seule : changer d'écran en admin ne doit pas démonter
 * l'application d'administration (et recharger session, menus et données).
 */
function AnimatedRoutes() {
  const location = useLocation()
  const reduce = useReducedMotion()
  const key = location.pathname.startsWith('/admin') ? '/admin' : location.pathname

  const routes = (
      <Routes location={location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/billets" element={<TicketsPage />} />
        {/* Les rubriques : l'accueil ne garde que l'essentiel et le billet. */}
        <Route path="/carte" element={<MenuPage />} />
        {/* « Personnaliser cette table » : /personnaliser?table=…&package=… */}
        <Route path="/personnaliser" element={<TableCustomizePage />} />
        <Route path="/programme" element={<ProgramPage />} />
        <Route path="/resultats" element={<ResultsPage />} />
        <Route path="/infos" element={<InfoPage />} />
        <Route path="/equipe" element={<TeamPage />} />
        {/* Les documents légaux. Une route par document : on doit pouvoir
            envoyer le lien des CGV à quelqu'un, pas une ancre. */}
        <Route path="/cgv" element={<LegalPage blockKey="legal.terms" />} />
        <Route path="/confidentialite" element={<LegalPage blockKey="legal.privacy" />} />
        <Route path="/mentions-legales" element={<LegalPage blockKey="legal.imprint" />} />
        <Route
          path="/reglement-concours"
          element={<LegalPage blockKey="legal.contest" />}
        />
        {/* Les droits RGPD : la demande, et l'atterrissage du lien de
            confirmation envoyé par e-mail (/mes-donnees?token=…). */}
        <Route path="/mes-donnees" element={<MyDataPage />} />
        {/* L'avis des invités, anonyme : le QR de la salle et le lien de
            l'e-mail de remerciement pointent ici (/avis?edition=<slug>). */}
        <Route path="/avis" element={<FeedbackPage />} />
        {/* L'avis des invités, anonyme : le QR de la salle et le lien de
            l'e-mail de remerciement pointent ici (/avis?edition=<slug>). */}
        <Route path="/avis" element={<FeedbackPage />} />
        {/* Le QR posé sur chaque table pointe ici : /commander?table=A3 */}
        {/* Rubrique du site : l'en-tête porte déjà le sélecteur de langue. */}
        <Route path="/commander" element={<OnsitePage />} />
        <Route
          path="/commande/:orderId"
          element={
            <WithLanguageBar>
              <OrderPage />
            </WithLanguageBar>
          }
        />
        <Route
          path="/mon-billet"
          element={
            <WithLanguageBar>
              <MyTicketsPage />
            </WithLanguageBar>
          }
        />
        <Route path="/concours" element={<ApplyPage />} />
        <Route path="/candidats" element={<CandidatesPage />} />
        <Route
          path="/vote"
          element={
            <WithLanguageBar>
              <VotePage />
            </WithLanguageBar>
          }
        />
        <Route
          path="/mes-resultats"
          element={
            <WithLanguageBar>
              <MyResultsPage />
            </WithLanguageBar>
          }
        />
        {/* La page de notation d'un juré : son lien personnel pointe ici. */}
        <Route
          path="/jury"
          element={
            <WithLanguageBar>
              <JuryPage />
            </WithLanguageBar>
          }
        />
        <Route path="/ops/checkin" element={<CheckinPage />} />
        <Route path="/ops/deroule" element={<ConductorPage />} />
        <Route path="/ops/live" element={<LivePage />} />
        <Route path="/ops/bilan" element={<AnalyticsPage />} />
        <Route path="/ops/cuisine" element={<StationPage station="kitchen" />} />
        <Route path="/ops/bar" element={<StationPage station="bar" />} />
        <Route path="/ops/caisse" element={<CashPage />} />
        {/* L'espace du serveur : ses tables, ses commandes, ce qu'il a en
            poche. Sur son téléphone, une main libre. */}
        <Route path="/ops/service" element={<ServicePage />} />
        {/* Le poste de vente : cartes larges, panier collé en bas, deux
            gestes pour encaisser. Tenu d'une main, dans le bruit. */}
        <Route path="/ops/pos" element={<PosPage />} />
        <Route path="/ops/stock" element={<InventoryPage />} />
        {/* La télé : un coin discret qui s'efface — ou `?lang=xx` dans son lien. */}
        <Route
          path="/ecran"
          element={
            <>
              <ScreenPage />
              <IdleLanguageCorner />
            </>
          }
        />
        {/* Le back-office : porte d'authentification et coquille communes. */}
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
  )

  if (reduce) return routes

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={key}
        initial={{ opacity: 0, y: 14, filter: 'blur(6px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)', transitionEnd: { filter: 'none' } }}
        exit={{ opacity: 0, y: -8, filter: 'blur(4px)' }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        {routes}
      </motion.div>
    </AnimatePresence>
  )
}
